import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Hammer, Loader2 } from 'lucide-react';
import BuildQuoteCard from '../components/Build/BuildQuoteCard';
import {
  PROJECT_TYPE_OPTIONS,
  CONSULTATION_HOUR_OPTIONS,
  CONSULTATION_ONLY_HOUR_OPTIONS,
  CONSULTATION_HOURLY_USD,
  CONSULTATION_FOUNDER,
  CONSULTATION_FOUNDER_TITLE,
  isConsultationProjectType,
  type BuildIntake,
  type BuildQuoteResult,
  type ProjectType,
  type ScopeSize,
} from '../lib/buildQuote/types';
import { fetchBuildQuote, saveBuildQuote, parseConversationNotes, analyzeBuildIntake } from '../lib/buildQuote/api';
import { getBuildIntakeErrors } from '../lib/buildQuote/validateIntake';
import type { ConversationTurn } from '../lib/buildQuote/types';
import {
  BUILD_CONVERSATION_EVENT,
  BUILD_FILL_INTAKE_EVENT,
  mergeIntakePatch,
  type BuildFillIntakeDetail,
} from '../lib/buildIntakeBridge';

const PLATFORM_OPTIONS = ['Web', 'iOS', 'Android', 'Desktop', 'API only'];

const SELECT_CLASS =
  'mt-1 w-full rounded-xl border border-white/15 bg-black px-4 py-3 text-white [color-scheme:dark]';

const defaultIntake = (projectType: ProjectType = 'web-app'): BuildIntake => ({
  projectType,
  projectName: '',
  company: '',
  contactName: '',
  email: '',
  description: '',
  platforms: ['Web'],
  scopeSize: 'medium',
  needsDesign: true,
  needsAuth: false,
  needsPayments: false,
  needsAI: false,
  needsAdminDashboard: false,
  timeline: 'standard',
  consultationHours: projectType === 'consultation' ? 1 : 0,
  conversationNotes: '',
});

function initialProjectTypeFromSearch(search: string): ProjectType {
  const type = new URLSearchParams(search).get('type');
  if (type === 'consultation') return 'consultation';
  return 'web-app';
}

export default function BuildPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [intake, setIntake] = useState<BuildIntake>(() =>
    defaultIntake(initialProjectTypeFromSearch(searchParams.toString()))
  );
  const [quote, setQuote] = useState<BuildQuoteResult | null>(null);
  const [quoteSource, setQuoteSource] = useState<'llm' | 'rules' | null>(null);
  const [avatarTurns, setAvatarTurns] = useState<ConversationTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoFilling, setAutoFilling] = useState(false);
  const [readyForQuote, setReadyForQuote] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const analyzeTimerRef = useRef<number | null>(null);
  const intakeRef = useRef(intake);
  intakeRef.current = intake;

  const conversationHistory = useMemo(() => {
    const fromNotes = parseConversationNotes(intake.conversationNotes || '');
    return [...avatarTurns, ...fromNotes];
  }, [intake.conversationNotes, avatarTurns]);

  const applyIntakePatch = useCallback((patch: Partial<BuildIntake>) => {
    setIntake((prev) => mergeIntakePatch(prev, patch));
    setQuote(null);
  }, []);

  const syncFromConversation = useCallback(
    async (history = conversationHistory) => {
      if (history.length < 1) return;
      setAutoFilling(true);
      setError(null);
      try {
        const result = await analyzeBuildIntake(history, intakeRef.current);
        applyIntakePatch(result.patch);
        setReadyForQuote(result.readyForQuote);
        setMissingFields(result.missingFields);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not sync form from conversation');
      } finally {
        setAutoFilling(false);
      }
    },
    [applyIntakePatch, conversationHistory]
  );

  useEffect(() => {
    const onAvatarTurn = (event: Event) => {
      const detail = (event as CustomEvent<ConversationTurn>).detail;
      if (!detail?.content?.trim()) return;
      setAvatarTurns((prev) => [...prev, detail]);
      setQuote(null);
    };
    const onFillIntake = (event: Event) => {
      const detail = (event as CustomEvent<BuildFillIntakeDetail>).detail;
      if (!detail?.intake) return;
      applyIntakePatch(detail.intake);
    };
    const onPostMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== BUILD_FILL_INTAKE_EVENT) return;
      if (data.intake) applyIntakePatch(data.intake as Partial<BuildIntake>);
    };

    window.addEventListener(BUILD_CONVERSATION_EVENT, onAvatarTurn);
    window.addEventListener(BUILD_FILL_INTAKE_EVENT, onFillIntake);
    window.addEventListener('message', onPostMessage);
    return () => {
      window.removeEventListener(BUILD_CONVERSATION_EVENT, onAvatarTurn);
      window.removeEventListener(BUILD_FILL_INTAKE_EVENT, onFillIntake);
      window.removeEventListener('message', onPostMessage);
    };
  }, [applyIntakePatch]);

  useEffect(() => {
    if (avatarTurns.length < 2) return;
    if (analyzeTimerRef.current) window.clearTimeout(analyzeTimerRef.current);
    analyzeTimerRef.current = window.setTimeout(() => {
      const history = [
        ...avatarTurns,
        ...parseConversationNotes(intake.conversationNotes || ''),
      ];
      void syncFromConversation(history);
    }, 1800);
    return () => {
      if (analyzeTimerRef.current) window.clearTimeout(analyzeTimerRef.current);
    };
  }, [avatarTurns, intake.conversationNotes, syncFromConversation]);

  const update = <K extends keyof BuildIntake>(key: K, value: BuildIntake[K]) => {
    setIntake((prev) => ({ ...prev, [key]: value }));
    setQuote(null);
  };

  const handleProjectTypeChange = (type: ProjectType) => {
    setIntake((prev) => ({
      ...prev,
      projectType: type,
      consultationHours:
        type === 'consultation'
          ? prev.consultationHours > 0
            ? prev.consultationHours
            : 1
          : prev.consultationHours,
    }));
    setQuote(null);
  };

  const isConsultationOnly = isConsultationProjectType(intake.projectType);

  const togglePlatform = (p: string) => {
    setIntake((prev) => {
      const platforms = prev.platforms.includes(p)
        ? prev.platforms.filter((x) => x !== p)
        : [...prev.platforms, p];
      return { ...prev, platforms: platforms.length ? platforms : [p] };
    });
    setQuote(null);
  };

  const handleGenerate = useCallback(async () => {
    const intakeErrors = getBuildIntakeErrors(intake);
    if (intakeErrors.length > 0) {
      setError(intakeErrors.join(' · '));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await fetchBuildQuote(intake, 0, conversationHistory);
      setQuote(result.quote);
      setQuoteSource(result.source ?? (result.quote.llmGenerated ? 'llm' : 'rules'));
      if (result.intake) {
        setIntake((prev) => ({ ...prev, ...result.intake }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate quote');
    } finally {
      setLoading(false);
    }
  }, [intake, conversationHistory]);

  const handleSave = async () => {
    if (!quote) return;
    const intakeErrors = getBuildIntakeErrors(intake);
    if (intakeErrors.length > 0) {
      setError(intakeErrors.join(' · '));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveBuildQuote(intake, quote, conversationHistory);
      navigate(`/build/quote/${saved.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save quote');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Build with SELF Labs — Project quote</title>
        <meta
          name="description"
          content="Get a strict, itemized quote for your mobile app, web app, AI agent, or software project — priced from your requirements. Save your quote or pay a 30% deposit to start."
        />
      </Helmet>

      <div id="build" data-avatar-target="build" className="min-h-screen bg-black text-white">
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <Link
              to="/products"
              className="group inline-flex items-center text-sm text-white/50 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
              Back to Products
            </Link>

            <section className="mt-10 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Hammer className="h-3.5 w-3.5" />
                Custom build
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                Get your project quote
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-white/65">
                Talk to Milan about your project — he&apos;ll ask questions and the intake form
                fills in as you go. Then generate a realistic AI quote and pay via Stripe.
              </p>
            </section>

            <div className="mt-12 grid gap-10 lg:grid-cols-2">
              <div
                className="space-y-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8"
                data-build-intake-form
                data-avatar-content="build-detail"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold">Project intake</h2>
                  {autoFilling ? (
                    <span className="inline-flex items-center gap-2 text-xs text-white/50">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Milan is filling the form…
                    </span>
                  ) : avatarTurns.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => void syncFromConversation()}
                      className="text-xs text-white/55 underline hover:text-white"
                    >
                      Sync from conversation
                    </button>
                  ) : null}
                </div>

                {missingFields.length > 0 && (
                  <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/50">
                    Milan still needs: {missingFields.join(', ')}
                  </p>
                )}

                {readyForQuote && (
                  <p className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-200/90">
                    Form looks ready — click Generate quote when you are.
                  </p>
                )}

                <label className="block text-sm">
                  <span className="text-white/55">Project type</span>
                  <select
                    name="projectType"
                    data-avatar-field="projectType"
                    value={intake.projectType}
                    onChange={(e) => handleProjectTypeChange(e.target.value as ProjectType)}
                    className={SELECT_CLASS}
                  >
                    {PROJECT_TYPE_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id} className="bg-black text-white">
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {isConsultationOnly && (
                    <p className="mt-1 text-xs text-white/45">
                      1-on-1 with {CONSULTATION_FOUNDER}, {CONSULTATION_FOUNDER_TITLE} — $
                      {CONSULTATION_HOURLY_USD}/hr
                    </p>
                  )}
                </label>

                {isConsultationOnly && (
                  <label className="block text-sm">
                    <span className="text-white/55">Hours to book</span>
                    <select
                      name="consultationHours"
                      data-avatar-field="consultationHours"
                      value={intake.consultationHours}
                      onChange={(e) =>
                        update('consultationHours', Number(e.target.value))
                      }
                      className={SELECT_CLASS}
                    >
                      {CONSULTATION_ONLY_HOUR_OPTIONS.map((h) => (
                        <option key={h} value={h} className="bg-black text-white">
                          {h} hour{h === 1 ? '' : 's'} — $
                          {(h * CONSULTATION_HOURLY_USD).toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="text-white/55">Your name</span>
                    <input
                      name="contactName"
                      data-avatar-field="contactName"
                      value={intake.contactName}
                      onChange={(e) => update('contactName', e.target.value)}
                      className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-white/55">Email</span>
                    <input
                      type="email"
                      name="email"
                      data-avatar-field="email"
                      value={intake.email}
                      onChange={(e) => update('email', e.target.value)}
                      className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                    />
                  </label>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="text-white/55">Company</span>
                    <input
                      name="company"
                      data-avatar-field="company"
                      value={intake.company}
                      onChange={(e) => update('company', e.target.value)}
                      className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-white/55">Project name</span>
                    <input
                      name="projectName"
                      data-avatar-field="projectName"
                      value={intake.projectName}
                      onChange={(e) => update('projectName', e.target.value)}
                      className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                    />
                  </label>
                </div>

                <label className="block text-sm">
                  <span className="text-white/55">
                    {isConsultationOnly ? 'What do you want to discuss?' : 'What are you building?'}
                  </span>
                  <textarea
                    name="description"
                    data-avatar-field="description"
                    value={intake.description}
                    onChange={(e) => update('description', e.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                    placeholder={
                      isConsultationOnly
                        ? 'Product strategy, architecture review, scoping a build, technical direction…'
                        : 'Features, users, integrations, success criteria…'
                    }
                  />
                  <p className="mt-1 text-xs text-white/40">
                    {intake.description.trim().length}/20 characters minimum
                  </p>
                </label>

                <label className="block text-sm">
                  <span className="text-white/55">Conversation with Milan (optional)</span>
                  <textarea
                    value={intake.conversationNotes || ''}
                    onChange={(e) => update('conversationNotes', e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm"
                    placeholder="Paste key points from your chat — e.g. Client: need iOS + Stripe. Milan: we'll scope auth and payments."
                  />
                  {avatarTurns.length > 0 && (
                    <p className="mt-1 text-xs text-white/40">
                      {avatarTurns.length} turn(s) captured from avatar chat
                    </p>
                  )}
                </label>

                {!isConsultationOnly && (
                  <>
                <div>
                  <span className="text-sm text-white/55">Platforms</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {PLATFORM_OPTIONS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => togglePlatform(p)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          intake.platforms.includes(p)
                            ? 'border-white bg-white text-black'
                            : 'border-white/20 text-white/60 hover:border-white/40'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="block text-sm">
                  <span className="text-white/55">Scope size</span>
                  <select
                    name="scopeSize"
                    data-avatar-field="scopeSize"
                    value={intake.scopeSize}
                    onChange={(e) => update('scopeSize', e.target.value as ScopeSize)}
                    className={SELECT_CLASS}
                  >
                    <option value="small" className="bg-black text-white">
                      Small — focused MVP
                    </option>
                    <option value="medium" className="bg-black text-white">
                      Medium — standard product
                    </option>
                    <option value="large" className="bg-black text-white">
                      Large — complex / multi-module
                    </option>
                  </select>
                </label>

                <div className="grid gap-2 sm:grid-cols-2">
                  {(
                    [
                      ['needsDesign', 'Product design'],
                      ['needsAuth', 'Auth & users'],
                      ['needsPayments', 'Payments'],
                      ['needsAI', 'AI / agents'],
                      ['needsAdminDashboard', 'Admin dashboard'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-white/65">
                      <input
                        type="checkbox"
                        checked={intake[key]}
                        onChange={(e) => update(key, e.target.checked)}
                        className="rounded border-white/30"
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <label className="block text-sm">
                  <span className="text-white/55">Timeline</span>
                  <select
                    name="timeline"
                    data-avatar-field="timeline"
                    value={intake.timeline}
                    onChange={(e) =>
                      update('timeline', e.target.value as 'standard' | 'rush')
                    }
                    className={SELECT_CLASS}
                  >
                    <option value="standard" className="bg-black text-white">
                      Standard
                    </option>
                    <option value="rush" className="bg-black text-white">
                      Expedited (+15%)
                    </option>
                  </select>
                </label>
                  </>
                )}

                {!isConsultationOnly && (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-sm font-medium text-white/80">Founder consultation (optional)</p>
                  <p className="mt-1 text-xs text-white/50">
                    1-on-1 strategy sessions with {CONSULTATION_FOUNDER},{' '}
                    {CONSULTATION_FOUNDER_TITLE} — ${CONSULTATION_HOURLY_USD}/hr
                  </p>
                  <label className="mt-3 block text-sm">
                    <span className="text-white/55">Hours to book</span>
                    <select
                      name="consultationHours"
                      data-avatar-field="consultationHours"
                      value={intake.consultationHours}
                      onChange={(e) =>
                        update('consultationHours', Number(e.target.value))
                      }
                      className={SELECT_CLASS}
                    >
                      {CONSULTATION_HOUR_OPTIONS.map((h) => (
                        <option key={h} value={h} className="bg-black text-white">
                          {h === 0
                            ? 'None'
                            : `${h} hour${h === 1 ? '' : 's'} — $${(h * CONSULTATION_HOURLY_USD).toLocaleString()}`}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                )}

                {error && (
                  <p className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-white py-3.5 font-semibold text-black hover:bg-white/90 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Analyzing with AI…
                    </>
                  ) : (
                    'Generate quote'
                  )}
                </button>
              </div>

              <div>
                {quote ? (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <BuildQuoteCard quote={quote} />
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 rounded-xl bg-white py-3 font-semibold text-black hover:bg-white/90 disabled:opacity-50"
                      >
                        {saving ? 'Saving…' : 'Save quote & checkout'}
                      </button>
                    </div>
                    <p className="mt-3 text-center text-xs text-white/40">
                      Saving gives you a shareable link and unlocks deposit checkout via Stripe.
                      {quoteSource === 'rules' && (
                        <span className="block mt-1 text-white/35">
                          AI unavailable — estimate used standard pricing rules.
                        </span>
                      )}
                    </p>
                  </motion.div>
                ) : (
                  <div className="flex h-full min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-white/15 p-8 text-center text-white/45">
                    Fill out the intake form and click Generate quote to see your itemized
                    estimate.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
